import BrowserEnvironmentResolver from '@/helpers/environment.helper'
import { getAppMetadata } from '@/plan/metadata/metadata'
import {
  getIdsForBrands,
  getIdsForSubCategories,
  getIdsForTopCategories,
} from '@/services/pois-tileset.service'
import { POI_LAYER } from '../constant'
import layerService from '@/layers/layerService'
import { milesToMeter } from '@/utils/metersToPixels'

/**
 * POIs Filter Cache System
 *
 * This module provides a high-performance cache for POI filter values
 * that gets updated when the Vuex store changes, but allows direct
 * access during filtering without going through the store.
 */
export interface POIsFilterCacheProximityElements {
  subCategories: number[]
  topCategories: number[]
  brands: number[]
  locations: string[]
}

interface POIsFilterCache {
  proximity: POIsFilterCacheProximityElements
  bufferDistance: number
}

interface Filter {
  proximity: {
    min_distance: number
    max_distance: number
  }
}
interface PlanProximity {
  proximity: {
    categories: string[]
    brands: string[]
    locations: string[]
  }
  filters: Filter[]
}

const DEFAULT_CACHE: POIsFilterCache = {
  proximity: {
    subCategories: [],
    topCategories: [],
    brands: [],
    locations: [],
  },
  bufferDistance: -1,
}

class POIsFilterCacheManager {
  private cache: POIsFilterCache = DEFAULT_CACHE
  private offset = 0

  /**
   * Updates the cache with new filter values
   * Called when the plan changes in Vuex store
   */
  async updateCache(plan: PlanProximity): Promise<void> {
    const metadata = getAppMetadata()
    const environmentResolver = BrowserEnvironmentResolver.getInstance()

    const [subCategories, topCategories, brands] = await Promise.all([
      getIdsForSubCategories(
        metadata,
        environmentResolver,
        plan.proximity?.categories || [],
      ),
      getIdsForTopCategories(
        metadata,
        environmentResolver,
        plan.proximity?.categories || [],
      ),
      getIdsForBrands(
        metadata,
        environmentResolver,
        plan.proximity?.brands || [],
      ),
    ])
    const maxBufferDistance = milesToMeter(
      plan.filters[0].proximity.max_distance,
    )

    const newCache: POIsFilterCache = {
      proximity: {
        subCategories,
        topCategories,
        brands,
        locations: plan.proximity?.locations || [],
      },
      bufferDistance: maxBufferDistance,
    }

    this.cache = newCache
  }

  /**
   * Gets the current cached filter values
   * This is the fast path used by filterPois function
   */
  getFiltersProximityElements(): POIsFilterCacheProximityElements {
    return this.cache.proximity
  }

  getBufferDistance(): number {
    return this.cache.bufferDistance
  }

  /**
   * Clears the cache (useful for testing or reset scenarios)
   */
  clearCache(): void {
    this.cache = DEFAULT_CACHE
  }

  isCacheEmpty(): boolean {
    const proximityElements = this.cache.proximity
    return (
      proximityElements.subCategories.length === 0 &&
      proximityElements.topCategories.length === 0 &&
      proximityElements.brands.length === 0 &&
      proximityElements.locations.length === 0
    )
  }
}

// Singleton instance
const poisFilterCache = new POIsFilterCacheManager()

/**
 * Updates the POIs filter cache when plan changes
 * This should be called from Vuex mutations
 */
export async function updatePOIsFilterCache(
  plan: PlanProximity,
): Promise<void> {
  // eslint-disable-next-line no-console
  console.debug('🟪 Updating POIs filter cache tileset')
  await poisFilterCache.updateCache(plan)
  // eslint-disable-next-line no-console
  console.debug('🟪 POIs filter cache tileset updated')
  if (poisFilterCache.isCacheEmpty()) {
    layerService.hideLayer(POI_LAYER, true)
  } else {
    const isLayervisible = layerService.isVisible(POI_LAYER)
    if (!isLayervisible) {
      return
    }
    console.debug('🟪 Updating POI layer after cache for tileset is updated')
    // Trigger POI layer update after cache is ready
    layerService.updateLayer(POI_LAYER, {
      updateTriggers: {
        getFilterValue: Date.now(),
      },
    })
  }
}

/**
 * Gets cached POI filter values for high-performance filtering
 * This bypasses Vuex store access during filtering
 */
export function getCachedPOIsFilters(): POIsFilterCacheProximityElements {
  return poisFilterCache.getFiltersProximityElements()
}

export function getCachedPOIsBufferDistance(): number {
  return poisFilterCache.getBufferDistance()
}

export function isPOIsCacheEmpty(): boolean {
  return poisFilterCache.isCacheEmpty()
}
